import {Injectable} from '@angular/core';
import 'rxjs/add/operator/map';
import { HttpClient } from './http-client';
import {Response} from '@angular/http';
import {
  CotizacionAgrisViewModel,
  CotizacionesAgrisFilterViewModel,
  FiltersAgrisViewModel,
  InsumosViewModel,
  InsumoViewModel,
  OficinaComercialViewModel,
  SucursalComercialViewModel
} from '../components/agris/common/AgrisViewModels';
import {FileSaverService} from './file-saver-service';
import {BehaviorSubject} from 'rxjs';
import {PERM_ACCIONES_ID, PermisosService} from './permisos-service';
import {InsumosFilter} from '../components/agris/common/agris-barra-busqueda/agris-barra-busqueda.component';
import {NavigationEnd, Router} from '@angular/router';
import {MdSnackBar} from '@angular/material';

export const AgrisEstadosIds = {
  PENDIENTE: 1,
  GUARDADA: 2,
  ENVIADA_ACTIVA: 3,
  CONFIRMADA: 4,
  VENCIDA: 5,
  PERDIDA: 6,
  RENOVADA: 7
}

export const ModalidadFacturacionIds = {
  DIRECTA: '001',
  REVENTA: '002',
}

export const editedInsumosKey = 'AgrisEditedInsumos';

@Injectable()
export class AgrisService {

  // Observable source
  private oficinaSource = new BehaviorSubject<OficinaComercialViewModel>(null);
  private sucursalSource = new BehaviorSubject<SucursalComercialViewModel>(null);

  // Observable stream
  oficina$ = this.oficinaSource.asObservable();
  sucursal$ = this.sucursalSource.asObservable();

  // Observable source
  private listaOficinasSource = new BehaviorSubject<OficinaComercialViewModel[]>([]);

  // Observable stream
  oficinas$ = this.listaOficinasSource.asObservable();


  private oficinaSucursalSource = new BehaviorSubject<OficinaSucursal>(null);
  oficinaSucursal$ = this.oficinaSucursalSource.asObservable();

  private oficinasLoading = false;

  public cotizacionCreada = false;

  // usamos esta variable para mantener el estado force edit en el detalle de cotizacion
  public forceEdit = false;

  public filterInsumo: InsumosFilter = {
    rubro: null,
    subRubro: null,
    linea: null,
    familia: null
  }

  snackbarErrorAvailable = true;

  constructor(
    private httpClient: HttpClient,
    private fileSaverService: FileSaverService,
    private permisosService: PermisosService,
    private router: Router,
    private snackbar: MdSnackBar,
  ) {
    this.initializeOficinaSucursal();
    this.router.events
      .filter(event => event instanceof NavigationEnd)
      .subscribe((e:any) => {
        // si no nos encontramos en el detalle de una cotizacion o la pagina de nueva cotizacion
        // (en este caso se usaria la edicion) desactivamos la variable cotizacion creada
        if (e.url !== null && e.url.toString().search('agris-cotizacion') === -1 && e.url.toString().search('agris-nueva-cotizacion') === -1) {
          this.cotizacionCreada = false;
          // tambien eliminamos la entrada a localStorage de isnumos editados
          localStorage.removeItem(editedInsumosKey);
        }
      });
  }

  public nuevaCotizInsumo: InsumoViewModel = null;
  public editarCotizacion: CotizacionAgrisViewModel = null;
  public cantidadInsumo: number = 1;
  public insumoToAddToCotiz: InsumoViewModel = null;

  initializeOficinaSucursal(): void {
    if (this.oficinasLoading) {return;}

    // verificamos el permiso
    if (!this.permisosService.permisoAutorizado(PERM_ACCIONES_ID.P_Agris)) {return;}
    // solo lo cargamos una vez y luego lo manejamos con el componente
    if (this.listaOficinasSource.value.length) {return;}

    this.oficinasLoading = true;
    this.getOficinas().subscribe(x => {
      if (x && x.length) {
        this.listaOficinasSource.next(x);
        // antes de setear la primer oficina que llega, verificamos si hay una oficina guardada y levantamos la ultima usada
        const value = localStorage.getItem('AgrisOfiSucCache');
        const cache: OficinaSucursalCache = value !== null ? JSON.parse(value) : null;
        let ofiToSet = x[0];
        // verificamos que el cache este dentro de la lista de oficinas obtenidas
        if (cache !== null && x.findIndex(y => y.Id === cache.OfiId) !== -1) {
          ofiToSet = x.find(y => y.Id === cache.OfiId);
        }
        this.oficinaSource.next(ofiToSet);

        // cargar sucursal por primera vez
        this.getSucursales(ofiToSet.Id).subscribe(y => {
          if (y && y.length) {
            // antes de setear la primer sucursal, verificamos si se puede levantar de cache
            if (cache !== null && y.findIndex(z => z.Id === cache.SucId) !== -1) {
              this.setSucursal(y.find(z => z.Id === cache.SucId));
            } else {
              this.setSucursal(y[0]);
            }
          }
        });
      }
      this.oficinasLoading = false;
    }, err => this.oficinasLoading = false);

  }

  setOficina(ofi: OficinaComercialViewModel, loadFromCache = false, deafultLoad = false) {
    // verificamos que no sea la actual
    if (this.oficinaSource.value !== null && this.oficinaSource.value.Id === ofi.Id) {return;}
    this.oficinaSource.next(ofi);
  }

  setSucursal(suc: SucursalComercialViewModel) {
    this.sucursalSource.next(suc);
    this.oficinaSucursalSource.next({oficina: this.oficinaSource.value, sucursal: this.sucursalSource.value});
    const cache: OficinaSucursalCache = {
      OfiId: this.oficinaSource.value.Id,
      SucId: this.sucursalSource.value.Id,
    }
    localStorage.setItem('AgrisOfiSucCache', JSON.stringify(cache));
  }

  deleteOficinaSucursal() {
    this.listaOficinasSource.next([]);
    this.sucursalSource.next(null);
    this.oficinaSource.next(null);
    this.oficinaSucursalSource.next(null);
  }

  getOficinas() {
    return this.httpClient.get('/api/agris/oficinas')
      .map((response: Response) => {
        return response.json().ResponseData as OficinaComercialViewModel[];
      });
  }

  getSucursales(id: number) {
    return this.httpClient.post(`/api/agris/sucursales/${id}`, {})
      .map((response: Response) => {
        return response.json().ResponseData as SucursalComercialViewModel[];
      });
  }

  getFormasPago() {
    return this.httpClient.get('/api/agris/formas-de-pago')
      .map((response: Response) => {
        return response.json().ResponseData;
      });
  }

  getModalidadFacturacion() {
    return this.httpClient.get('/api/agris/modalidad-facturacion')
      .map((response: Response) => {
        return response.json().ResponseData;
      });
  }

  saveNuevaCotizacion(payload) {
    return this.httpClient.post('/api/agris', payload)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as CotizacionAgrisViewModel;
        }
      });
  }

  searchInsumos(payload: InsumosViewModel) {
    payload.filter = payload.filter.toUpperCase();
    return this.httpClient.post('/api/agris/insumos', payload)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData;
        }
      });
  }

  getInsumo(payload: InsumosViewModel) {
    return this.httpClient.post('/api/agris/get-insumo', payload)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as InsumoViewModel;
        }
      });
  }

  searchCotizaciones(payload: CotizacionesAgrisFilterViewModel) {
    return this.httpClient.post('/api/agris/filter', payload)
      .map((response: Response) => {
        if (response){
          const final = response.json().ResponseData as CotizacionAgrisViewModel[];
          if (final) {
            final.forEach(x => x.EstadosCotizacion.reverse())
          }
          return final;
        }
      });
  }

  getInsumosFilter() {
    return this.httpClient.get('/api/agris/get-filtros')
      .map((response: Response) => {
        return response.json().ResponseData as FiltersAgrisViewModel;
      });
  }

  guardarCotizacion(payload: CotizacionAgrisViewModel) {
    return this.httpClient.put('/api/agris', payload)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as CotizacionAgrisViewModel;
        }
      });
  }

  countPendientes() {
    return this.httpClient.post('/api/agris/count-pendientes', {})
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as number;
        }
      });
  }

  editarDatosCotizacion(payload: CotizacionAgrisViewModel) {
    return this.httpClient.put('/api/agris/edit', payload)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as CotizacionAgrisViewModel;
        }
      });
  }

  downloadFile(id: number) {
    return this.httpClient.getFiles(`/api/agris/get-attachment/${id}`, null)
      .map((response: Response ) => {
        return this.fileSaverService.SaveToFileSystem(response);
      });
  }

  renovarCotizacion(cot: CotizacionAgrisViewModel) {
    return this.httpClient.post('/api/agris/renovar', cot)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as CotizacionAgrisViewModel;
        }
      });
  }

  showSnackbarAPIError() {
    // dado que muchas operaciones de API suceden a la vez, mandamos solo un mje de error mientras ya esté abierto
    if (this.snackbarErrorAvailable) {
      this.snackbarErrorAvailable = false;
      this.snackbar.open(
        'Se ha producido un error al procesar la solicitud.',
        null,
        {duration: 3000}
      );
      setTimeout(x => {
        this.snackbarErrorAvailable = true;
      }, 3000)
    }

  }

  perderCotizacion(payload) {
    return this.httpClient.put('/api/agris/marcar-perdida', payload)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as CotizacionAgrisViewModel;
        }
      });
  }

  generarPNJDE(id: number) {
    return this.httpClient.post(`/api/agris/generar-PN-JDE/${id}`, {})
      .map((response: Response) => {
        return response.json().ResponseData as CotizacionAgrisViewModel;
      });
  }

  getActividadCotizacion(id: number, pageSize: number, pageNumber: number) {
    return this.httpClient.get(`/api/actividad/cotizacion-agris/${id}/${pageSize}/${pageNumber}`)
      .map((response: Response) => {
        if (response){
          return response.json().ResponseData as ActividadAgrisViewModel[];
        }
      });
  }

}

interface OficinaSucursalCache {
  OfiId: number,
  SucId: number
}

export interface OficinaSucursal {
  oficina: OficinaComercialViewModel;
  sucursal: SucursalComercialViewModel;
}

export interface ActividadAgrisViewModel {
  ID: number;
  FechaHora: number;
  ActivityObject: ActividadCotizacionAgrisViewModel;
  IsRead: boolean;
}

interface ActividadCotizacionAgrisViewModel {
  ID: number;
  FechaHora: number;
  UserAccionID: number;
  UserNameAction: string;
  Accion: string;
  Entidad: string;
  Cot_ID: number;
}


// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/services/agris-service.ts